import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Save, Trash2, ShieldAlert, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceRulesAPI } from '../services/api';
import { getMasterCrudAccess } from '../lib/masterData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const DEFAULT_RULE_FORM = {
  name: '',
  rule_type: 'late_coming',
  grace_minutes: 0,
  threshold_count: 3,
  penalty_type: 'deduct_leave',
  penalty_value: 0.5,
  penalty_leave_type: 'casual',
  enable_short_leave_clubbing: false,
  reset_period: 'monthly',
  is_active: true,
};

const parseInteger = (value, fallback = 0) => {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseFloatValue = (value, fallback = 0.5) => {
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeRuleForm = (rule) => ({
  name: String(rule?.name || ''),
  rule_type: String(rule?.rule_type || 'late_coming'),
  grace_minutes: Number(rule?.grace_minutes ?? 0),
  threshold_count: Number(rule?.threshold_count ?? 3),
  penalty_type: String(rule?.penalty_type || 'deduct_leave'),
  penalty_value: Number(rule?.penalty_value ?? 0.5),
  penalty_leave_type: String(rule?.penalty_leave_type || 'casual'),
  enable_short_leave_clubbing: Boolean(rule?.enable_short_leave_clubbing),
  reset_period: String(rule?.reset_period || 'monthly'),
  is_active: Boolean(rule?.is_active ?? true),
});

const successState = (message) => ({
  attendanceRuleSuccessMessage: message,
  attendanceRuleSuccessAt: Date.now(),
});

const AdminAttendanceRuleCrudPage = ({ mode, ruleId = null, routeBasePath = '/admin/attendance-rules' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const access = useMemo(() => getMasterCrudAccess(user), [user]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_RULE_FORM);

  const currentRule = useMemo(
    () => rules.find((row) => String(row.id) === String(ruleId)) || null,
    [rules, ruleId]
  );

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await attendanceRulesAPI.getRules();
      setRules(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load attendance rules'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  useEffect(() => {
    if (!currentRule || mode === 'create') {
      setForm(DEFAULT_RULE_FORM);
      return;
    }
    setForm(normalizeRuleForm(currentRule));
  }, [currentRule, mode]);

  const canProceed =
    mode === 'create'
      ? access.canCreate
      : mode === 'edit'
        ? access.canEdit
        : mode === 'delete'
          ? access.canDelete
          : access.canView;

  const pageTitle =
    mode === 'create'
      ? 'Create Attendance Rule'
      : mode === 'edit'
        ? 'Edit Attendance Rule'
        : mode === 'delete'
          ? 'Delete Attendance Rule'
          : 'View Attendance Rule';

  const handleBack = () => navigate(routeBasePath);

  const handleSave = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...form,
        name: form.name.trim(),
        grace_minutes: parseInteger(form.grace_minutes, 0),
        threshold_count: parseInteger(form.threshold_count, 3),
        penalty_value: parseFloatValue(form.penalty_value, 0.5),
      };
      if (mode === 'create') {
        await attendanceRulesAPI.createRule(payload);
        navigate(routeBasePath, { state: successState('Attendance rule created successfully') });
        return;
      }
      await attendanceRulesAPI.updateRule(Number(ruleId), payload);
      navigate(routeBasePath, { state: successState('Attendance rule updated successfully') });
    } catch (error) {
      toast.error(getErrorMessage(error, mode === 'create' ? 'Failed to create attendance rule' : 'Failed to update attendance rule'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await attendanceRulesAPI.deleteRule(Number(ruleId));
      navigate(routeBasePath, { state: successState('Attendance rule deleted successfully') });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete attendance rule'));
    } finally {
      setSaving(false);
    }
  };

  if (!canProceed) {
    return (
      <Card>
        <CardHeader><CardTitle>Access Restricted</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">You do not have permission to open this page.</p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  if ((mode === 'view' || mode === 'edit' || mode === 'delete') && !loading && !currentRule) {
    return (
      <Card>
        <CardHeader><CardTitle>Attendance Rule Not Found</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">The selected attendance rule is not available.</p>
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{pageTitle}</h2>
          <p className="text-sm text-muted-foreground">Dedicated attendance rule workflow with routed CRUD pages.</p>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'view' && access.canEdit ? (
            <Button variant="outline" onClick={() => navigate(`${routeBasePath}/${ruleId}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          ) : null}
          {mode === 'view' && access.canCreate ? (
            <Button variant="outline" onClick={() => navigate(`${routeBasePath}/new`)}>
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          ) : null}
          <Button variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-foreground">{pageTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : null}

          {!loading && mode === 'view' && currentRule ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rule Name</p>
                <p className="font-medium">{currentRule.name}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rule Type</p>
                <p>{currentRule.rule_type}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Grace Minutes</p>
                <p>{currentRule.grace_minutes}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Threshold Count</p>
                <p>{currentRule.threshold_count}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Penalty Type</p>
                <p>{currentRule.penalty_type}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Penalty Value</p>
                <p>{currentRule.penalty_value}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Penalty Leave Type</p>
                <p>{currentRule.penalty_leave_type || '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Reset Period</p>
                <p>{currentRule.reset_period}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Short Leave Clubbing</p>
                <p>{currentRule.enable_short_leave_clubbing ? 'Enabled' : 'Disabled'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</p>
                <Badge variant={currentRule.is_active ? 'success' : 'secondary'}>
                  {currentRule.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {access.canDelete ? (
                <div className="md:col-span-2">
                  <Button variant="destructive" onClick={() => navigate(`${RULE_ROUTE_BASE}/${ruleId}/delete`)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {!loading && (mode === 'create' || mode === 'edit') ? (
            <form className="space-y-6" onSubmit={handleSave}>
              <Card className="border-border bg-muted/30">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
                      <SlidersHorizontal className="h-4.5 w-4.5" />
                    </span>
                    <div>
                      <CardTitle className="text-foreground">Rule Definition</CardTitle>
                      <p className="text-sm text-muted-foreground">Identify the rule and the condition that triggers it.</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2 sm:col-span-2 lg:col-span-3">
                    <Label>Rule Name</Label>
                    <Input value={form.name} onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Rule Type</Label>
                    <Select value={form.rule_type} onValueChange={(value) => setForm((current) => ({ ...current, rule_type: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="late_coming">Late Coming</SelectItem>
                        <SelectItem value="early_exit">Early Exit</SelectItem>
                        <SelectItem value="short_leave">Short Leave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Grace Minutes</Label>
                    <Input type="number" min="0" value={form.grace_minutes} onChange={(e) => setForm((current) => ({ ...current, grace_minutes: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Threshold Count</Label>
                    <Input type="number" min="1" value={form.threshold_count} onChange={(e) => setForm((current) => ({ ...current, threshold_count: e.target.value }))} />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border bg-muted/30">
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-danger/10 text-danger">
                      <ShieldAlert className="h-4.5 w-4.5" />
                    </span>
                    <div>
                      <CardTitle className="text-foreground">Penalty &amp; Reset</CardTitle>
                      <p className="text-sm text-muted-foreground">Define the consequence and how counters reset over time.</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label>Penalty Type</Label>
                    <Select
                      value={form.penalty_type}
                      onValueChange={(value) => setForm((current) => ({ ...current, penalty_type: value }))}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deduct_leave">Deduct Leave</SelectItem>
                        <SelectItem value="deduct_salary">Deduct Salary</SelectItem>
                        <SelectItem value="convert_to_short_leave">Convert to Short Leave</SelectItem>
                        <SelectItem value="convert_to_half_day">Convert to Half Day</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Penalty Value</Label>
                    <Input type="number" step="0.5" min="0" value={form.penalty_value} onChange={(e) => setForm((current) => ({ ...current, penalty_value: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Reset Period</Label>
                    <Select value={form.reset_period} onValueChange={(value) => setForm((current) => ({ ...current, reset_period: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.penalty_type === 'deduct_leave' ? (
                    <div className="space-y-2">
                      <Label>Penalty Leave Type</Label>
                      <Select
                        value={form.penalty_leave_type || 'casual'}
                        onValueChange={(value) => setForm((current) => ({ ...current, penalty_leave_type: value }))}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="casual">Casual Leave</SelectItem>
                          <SelectItem value="sick">Sick Leave</SelectItem>
                          <SelectItem value="annual">Annual Leave</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
                    <Checkbox
                      id="attendance-rule-short-leave-clubbing"
                      checked={Boolean(form.enable_short_leave_clubbing)}
                      onCheckedChange={(checked) => setForm((current) => ({ ...current, enable_short_leave_clubbing: Boolean(checked) }))}
                    />
                    <Label htmlFor="attendance-rule-short-leave-clubbing">Enable short leave clubbing</Label>
                  </div>
                  <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
                    <Checkbox
                      id="attendance-rule-is-active"
                      checked={Boolean(form.is_active)}
                      onCheckedChange={(checked) => setForm((current) => ({ ...current, is_active: Boolean(checked) }))}
                    />
                    <Label htmlFor="attendance-rule-is-active">Active</Label>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Saving...' : mode === 'create' ? 'Create Rule' : 'Save Changes'}
                </Button>
                <Button type="button" variant="outline" onClick={handleBack}>Cancel</Button>
              </div>
            </form>
          ) : null}

          {!loading && mode === 'delete' && currentRule ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                <p className="font-medium text-destructive">This action deletes the selected attendance rule.</p>
                <p className="mt-1 text-sm text-muted-foreground">Review the rule before continuing.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rule Name</p>
                  <p className="font-medium">{currentRule.name}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Rule Type</p>
                  <p>{currentRule.rule_type}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="destructive" disabled={saving} onClick={handleDelete}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {saving ? 'Deleting...' : 'Delete Rule'}
                </Button>
                <Button variant="outline" onClick={handleBack}>Cancel</Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAttendanceRuleCrudPage;
